import { Button, Modal, Textarea, Group, Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { primaryColor } from '../../../../constants/theme';
import { useMutation } from '@tanstack/react-query';
import { useApp } from '../../../../context/AppContext';
import { cancelBooking } from '../../../../services/BookingService';
import {
  BookingResponseModel,
  CancelBookingInputModel,
} from '../../../../types/BookingModels';
import { ErrorModal } from '../error/ErrorModal';
import { BookingPreview } from './BookingPreview';

interface CancelBookingButtonProps {
  refreshBookings: () => void;
  profileId: string;
  groupedBookings: BookingResponseModel[];
  overrideText?: string;
  reloadOnSuccess?: boolean;
}

export const CancelBookingButton: React.FC<CancelBookingButtonProps> = ({
  refreshBookings,
  profileId,
  groupedBookings,
  overrideText,
  reloadOnSuccess,
}) => {
  const booking = groupedBookings[0];
  const { token } = useApp();
  const [opened, setOpened] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const isProfessional = profileId === booking?.professionalId;
  const isStartInFuture = new Date(booking.startIso) > new Date();
  const text = overrideText || 'Cancel';

  const {
    mutate: cancelBookingMutation,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      const cancelBookingInput: CancelBookingInputModel = {
        bookingIds: groupedBookings.map((b) => b.id),
        cancellationReason: cancellationReason,
      };
      await cancelBooking(cancelBookingInput, token || '');
    },
    onSuccess: () => {
      refreshBookings();
      setOpened(false);
      setCancellationReason('');
      if (reloadOnSuccess) {
        window.location.reload();
      }
    },
    onError: (error) => {
      console.error('Error cancelling booking:', error);
    },
  });

  if (
    !isStartInFuture ||
    booking.rejected ||
    booking.isCancelled ||
    (!booking.accepted && isProfessional)
  )
    return null;

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error cancelling the booking. Please try again."
        dismissAction={() => reset()}
        retryAction={() => cancelBookingMutation()}
        isLoading={isPending}
      />
      <Button
        radius="md"
        variant="outline"
        color={primaryColor}
        leftSection={<IconX size={16} />}
        onClick={() => setOpened(true)}
        data-testid={`cancel-button-${booking.id}`}
      >
        {text}
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
      >
        <Stack gap={10}>
          <Stack justify="center" align="center" gap={10}>
            <BookingPreview
              bookingType={isProfessional ? 'incoming' : 'outgoing'}
              title="Cancel Booking?"
              groupedBookings={groupedBookings}
              bottomText="Please note the booking will be automatically removed from your bookings page."
              titleTestId="cancel-booking-modal-title"
              textTestId="cancel-booking-modal-text"
            />
          </Stack>
          <Textarea
            mt={15}
            label="Cancellation Reason"
            placeholder="Cancellation Reason"
            rows={5}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
          <Group gap={10} justify="center">
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setOpened(false)}
              data-testid="cancel-modal-cancel-cta"
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={() => cancelBookingMutation()}
              loading={isPending}
              disabled={!cancellationReason}
              data-testid="cancel-modal-confirm-cta"
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
